"use client";

/**
 * Shared "+ Create Folder" action for any dropdown listing folders.
 *
 * Mirrors `useFieldCreateAction` (components/shared/fields/use-field-create-action.tsx):
 * it owns only the open-state and hosts a quick-create FolderDialog. Callers
 * keep their own folder list (FolderSelect owns its own server-side search;
 * MultiCombobox sites pass in their own `folderOptions`).
 *
 * Colocated with FolderSelect/FolderDialog (feature-owned, not shared/) since
 * folders are a single feature's concept — unlike fields, which are consumed
 * across recorder/automations/chat/dashboards/explore/folder.
 */

import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Plus } from "lucide-react";
import { FolderDialog } from "./folder-dialog";

interface UseFolderCreateActionOptions {
  /** Called with the newly created folder's id (e.g. to auto-select it). */
  onCreated: (folderId: string) => void;
}

interface FolderCreateAction {
  /** Shape-compatible with both Combobox.topAction and MultiCombobox.topAction. */
  topAction: {
    label: ReactNode;
    onSelect: () => void;
  };
  /** Render next to the dropdown that uses `topAction` — hosts the create dialog. */
  panel: ReactNode;
}

export function useFolderCreateAction({
  onCreated,
}: UseFolderCreateActionOptions): FolderCreateAction {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  return {
    topAction: {
      label: (
        <span className="flex items-center gap-2">
          <Plus className="h-4 w-4" /> {t("FOLDERS.CREATE_FOLDER")}
        </span>
      ),
      onSelect: () => setOpen(true),
    },
    panel: (
      <FolderDialog
        open={open}
        onClose={() => setOpen(false)}
        quickCreate
        onSuccess={(folderId) => {
          if (folderId) onCreated(folderId);
          setOpen(false);
        }}
      />
    ),
  };
}
